import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import { PageContainer, PageHeader, Table, Thead, Th, Td, TRow, EmptyState, Button, Input, Select, CategoryBadge, Muted, LinkButton } from "../ui";

interface Product {
  id: string;
  name: string;
  brand: string;
  category: string;
  case_configuration: string;
  vendor_name: string;
  oz_on_hand: number;
  oz_per_case: number;
  current_cost_per_case: number;
  is_active: boolean;
  sellable_formats: Array<{
    id: string;
    name: string;
    cash_price: number;
    card_price: number;
    package_type: string;
  }>;
}

export default function ProductList() {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";
  const [exporting, setExporting] = useState(false);

  // Every product, one row per sellable size, with UPC, cash and card price,
  // cost and units per case, so the whole catalog can be checked against
  // mPower in a spreadsheet instead of one product at a time.
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/products/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `karen-catalog-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Export failed: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setExporting(false);
    }
  };
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      if (search) params.set("search", search);
      params.set("is_active", "true");
      const response = await api.get(`/products?${params}`);
      setProducts(response.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [category, search]);

  // Debounced load — fires 300ms after search or category changes
  useEffect(() => {
    const timer = setTimeout(() => { loadProducts(); }, 300);
    return () => clearTimeout(timer);
  }, [loadProducts]);

  const categories = ["", "domestic", "import", "craft", "seltzer", "rtd", "malternative", "cider", "na", "accessory", "other"];

  return (
    <PageContainer>
      <PageHeader
        title="Products"
        subtitle={`${products.length} product${products.length !== 1 ? "s" : ""} in catalog`}
        action={
          <div className="flex gap-2">
            {isManager && (
              <Button variant="secondary" onClick={exportCsv} disabled={exporting}>
                {exporting ? "Preparing..." : "Export CSV"}
              </Button>
            )}
            <LinkButton to="/products/new">+ Add Product</LinkButton>
          </div>
        }
      />

      {/* Filters */}
      <form onSubmit={(e) => { e.preventDefault(); loadProducts(); }} className="flex gap-3 mb-6">
        <div className="flex-1">
          <Input
            value={search}
            onChange={setSearch}
            placeholder="Search by name, brand, SKU, or UPC..."
          />
        </div>
        <div className="w-48">
          <Select value={category} onChange={(val) => { setCategory(val); }}>
            {categories.map((c) => (
              <option key={c} value={c}>{c || "All Categories"}</option>
            ))}
          </Select>
        </div>
        <Button type="submit" variant="secondary">Search</Button>
      </form>

      {loading ? (
        <div className="text-center py-16"><Muted>Loading products...</Muted></div>
      ) : products.length === 0 ? (
        <EmptyState icon="&#127866;" message="No products found" detail="Try adjusting your search or add a new product" />
      ) : (
        <Table>
          <Thead>
            <Th>Product</Th>
            <Th>Category</Th>
            <Th>Vendor</Th>
            <Th align="right">On Hand</Th>
            {isManager && <Th align="right">Cost/Case</Th>}
            <Th align="right">Formats</Th>
            <Th>Actions</Th>
          </Thead>
          <tbody>
            {products.map((product, idx) => {
              const casesOnHand = Number(product.oz_per_case) > 0
                ? (Number(product.oz_on_hand) / Number(product.oz_per_case)).toFixed(1) : "0";
              const formats = typeof product.sellable_formats === "string"
                ? JSON.parse(product.sellable_formats) : product.sellable_formats || [];

              return (
                <TRow key={product.id} index={idx}>
                  <Td>
                    <Link to={`/products/${product.id}`} className="font-semibold hover:underline" style={{ color: "var(--rbw-navy)" }}>
                      {product.name}
                    </Link>
                    {product.brand && (
                      <div className="text-xs mt-0.5"><Muted>{product.brand}</Muted></div>
                    )}
                  </Td>
                  <Td><CategoryBadge category={product.category} /></Td>
                  <Td><Muted>{product.vendor_name || "--"}</Muted></Td>
                  <Td align="right" bold>
                    {casesOnHand} cases
                    <div className="text-xs font-normal"><Muted>{Number(product.oz_on_hand).toFixed(0)} oz</Muted></div>
                  </Td>
                  {isManager && <Td align="right" bold>${Number(product.current_cost_per_case).toFixed(2)}</Td>}
                  <Td align="right">
                    <span className="px-2.5 py-1 rounded-lg text-xs font-semibold" style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}>
                      {formats.length}
                    </span>
                  </Td>
                  <Td>
                    <div className="flex gap-1">
                      <Link to={`/products/${product.id}`} className="text-xs font-semibold px-2 py-1 rounded"
                        style={{ color: "var(--rbw-navy)" }}>Edit</Link>
                      <button onClick={async () => {
                        try {
                          const res = await api.post(`/products/${product.id}/duplicate`);
                          navigate(`/products/${res.data.id}`);
                        } catch { /* ignore */ }
                      }} className="text-xs font-semibold px-2 py-1 rounded"
                        style={{ color: "var(--rbw-blue-dark)" }}>Duplicate</button>
                    </div>
                  </Td>
                </TRow>
              );
            })}
          </tbody>
        </Table>
      )}
    </PageContainer>
  );
}
